/**
 * Processes tracking pixel code by replacing placeholders with GET params.
 *
 * This function searches for placeholders in the format
 * `[fromQueryParams:param1,param2,...]` within the `code` string and replaces
 * them with matching query parameters from `searchParams` if they are allowed
 * based on `JOB_TRACKING_PIXEL_WITH_REQUIRED_FROM_QUERY_PARAMS`.
 *
 * @param {string} code
 *   The original tracking pixel code with placeholder.
 * @param {URLSearchParams} searchParams
 *   The URL search parameters to check for matching values.
 * @param {string[]} requiredQueryParams
 *   An array of allowed query parameter names that can be used in the tracking
 *   pixel code.
 * @returns {string | null}
 *   Returns the processed tracking pixel code with valid parameters or
 *   `null` if no valid parameters are found.
 */
export function getProcessedTrackingPixelCode(
  code: string,
  searchParams: URLSearchParams,
  requiredQueryParams: string[] = [],
): string | null {
  if (!code) {
    return null;
  }

  if (requiredQueryParams.length === 0) {
    return code;
  }

  let hasValidParams = false;
  const processedCode = code.replace(
    /\[fromQueryParams:([^\]]+)\]/,
    (match, params) => {
      const paramList = params.split(',');
      const requiredParams = paramList
        .filter(
          (param) =>
            requiredQueryParams.includes(param.trim()) &&
            searchParams.has(param.trim()),
        )
        .map((param) => {
          const paramName = param.trim();
          const paramValue = searchParams.get(paramName) ?? '';

          // XSS protection: URL-encode the user-provided parameter value to
          // prevent injection of malicious code.
          return `${paramName}=${encodeURIComponent(paramValue)}`;
        });

      hasValidParams = requiredParams.length > 0;
      return hasValidParams ? requiredParams.join('&') : '';
    },
  );

  return hasValidParams ? processedCode : null;
}
